// =====================================================
// EVENT PARTICIPANTS TAB
// Stage 2: Participants & Pricing
// Tab component for managing event participants
// =====================================================

import { useState, useMemo, useCallback, useEffect, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  UserPlus, Users, DollarSign, FileSearch, TrendingUp, UserCog,
  Loader2, Download, FileUp, UserCheck, MoreHorizontal, Columns, Printer, ScanLine, Vote, Ban,
} from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { SortingState } from '@tanstack/react-table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { DataTableView } from '@/components/organisms/DataTableView';
import { getParticipantColumns } from './participantColumns';
import { ParticipantFilterBar } from './ParticipantFilterBar';
import { ParticipantCardList } from './ParticipantCardList';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { TOGGLEABLE_COLUMNS, type ParticipantTabFilters } from '../utils/participantFilters';
import {
  DEFAULT_PAGE_SIZE,
  formatSortParam,
  parsePageSizeParam,
  parseSortParam,
  sortParticipants,
} from '../utils/participantSort';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { getAllDistricts, getAllRegions } from '@/Services/regions.service';
import type { District } from '@/models/district';
import type { Region } from '@/models/region';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ParticipantRegistrationDialog } from './ParticipantRegistrationDialog';
import { BatchImportDialog } from './BatchImportDialog';
import { BulkCheckInDialog } from './BulkCheckInDialog';
import { PaymentModal } from './PaymentModal';
import { PaymentReversalModal } from './PaymentReversalModal';
import { PreregistrationReviewDialog, type ReviewQueue } from './PreregistrationReviewDialog';
import { BulkPrintDialog } from '../print/BulkPrintDialog';
import { BadgeStickerPrint } from '../print/BadgeStickers';
import { participantToSticker } from '../print/participantSticker';
import { ReceiptPrintPreview } from '@/features/receipts/components/ReceiptPrintPreview';
import { eventReceiptShare } from '../utils/receiptShare';
import { participantPhone } from '../utils/participantPhone';
import { getReceiptById } from '@/features/receipts/data/receiptsRepository';
import { receiptToPrintData, type Receipt } from '@/features/receipts/types';
import { publicReceiptUrl } from '@/features/receipts/utils/publicReceiptUrl';
import { getEventPayments } from '@/Services/eventPayments.service';
import { buildParticipantActions, type ParticipantReceiptInfo } from './participantActions';
import { setVotingEnabled } from '@/Services/eventParticipants.service';
import { useEventHasVoting } from '../hooks/useEventHasVoting';
import { EditRegistrationDialog } from './EditRegistrationDialog';
import { EditLinkDialog } from './EditLinkDialog';
import { needsSignature } from '../public/registrationEditLogic';
import { ParticipantRowContextMenu } from './ParticipantActionsMenu';
import { IssueEventReceiptPrompt } from './IssueEventReceiptPrompt';
import type { EventPaymentMethod } from '@/Services/eventPayments.service';
import type { Event, EventStatus } from '@/types/event.types';
import { useEventParticipants, useEventParticipantStatistics, useParticipantMutations, usePaymentMutations, useAttendanceMutations } from '../hooks/useEventParticipants';
import { eventParticipantsRepository } from '@/data/eventParticipantsRepository';
import { useToast } from '@/hooks/use-toast';
import { formatPrice } from '@/models/event';
import { formatBs } from '@/lib/money';
import { Money } from '@/components/ui/money';
import { exportParticipantsToCSV } from '../utils/participantExport';
import {
  getEventParticipants,
  getParticipantById,
  type ParticipantWithDetails,
} from '@/Services/eventParticipants.service';
import { usePermissions } from '@/hooks/usePermissions';
import { useAuth } from '@/context/authContext';
import { bulkCheckInParticipants } from '@/Services/eventAttendance.service';
import { bulkRecordPayments, PAYMENT_METHOD_LABELS, SELECTABLE_PAYMENT_METHODS } from '@/Services/eventPayments.service';

// ===================
// TYPES
// ===================

interface EventParticipantsTabProps {
  eventId: string;
  basePrice: number;
  eventName?: string;
  /** Event is OPEN — new registrations allowed */
  acceptsRegistrations?: boolean;
  eventStatus?: string;
  /** Full event — needed for printing forms */
  event?: Event;
  /** Lets the "event is in draft" notice offer to open registrations */
  canTransition?: boolean;
  onRequestStatus?: (target: EventStatus) => void;
}

interface StatCardProps {
  title: string;
  value: ReactNode;
  icon: React.ComponentType<{ className?: string }>;
  description?: string;
  onClick?: () => void;
}

// ===================
// STAT CARD COMPONENT
// ===================

function StatCard({ title, value, icon: Icon, description, onClick }: StatCardProps) {
  return (
    <Card
      className={onClick ? 'cursor-pointer transition-colors hover:bg-muted/50' : undefined}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
    >
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <Icon className="size-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
        {description && <p className="text-xs text-muted-foreground">{description}</p>}
      </CardContent>
    </Card>
  );
}

function StatCardSkeleton() {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div className="h-4 w-28 animate-pulse rounded bg-muted" />
        <div className="size-4 animate-pulse rounded bg-muted" />
      </CardHeader>
      <CardContent>
        <div className="mb-2 h-7 w-16 animate-pulse rounded bg-muted" />
        <div className="h-3 w-36 animate-pulse rounded bg-muted" />
      </CardContent>
    </Card>
  );
}

// ===================
// MAIN COMPONENT
// ===================

export function EventParticipantsTab({
  eventId,
  basePrice,
  eventName = 'evento',
  acceptsRegistrations = true,
  eventStatus,
  event,
  canTransition = false,
  onRequestStatus,
}: EventParticipantsTabProps) {
  // Payments + attendance stay available after registrations close (CLOSED),
  // but not while drafting or once the event is completed.
  const statusAllowsOps = eventStatus === undefined || eventStatus === 'OPEN' || eventStatus === 'CLOSED';
  const { can, canAny } = usePermissions();
  const isMobile = useMediaQuery('(max-width: 767px)');
  const canRegister = acceptsRegistrations && can('event_participant.register');
  const canOperate = statusAllowsOps && canAny('event_payment.record', 'event_payment.reverse', 'event_participant.checkin');

  // Filters — persisted in the URL (?pf=) so refresh / shared links keep them
  type Filters = ParticipantTabFilters;
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState<Filters>(() => {
    try {
      const raw = searchParams.get('pf');
      return raw ? { type: 'ALL', ...JSON.parse(raw) } : { type: 'ALL' };
    } catch {
      return { type: 'ALL' };
    }
  });
  useEffect(() => {
    const { type, ...rest } = filters;
    const meaningful = Object.fromEntries(
      Object.entries({ ...rest, ...(type && type !== 'ALL' ? { type } : {}) }).filter(([, v]) => v !== undefined && v !== ''),
    );
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (Object.keys(meaningful).length) next.set('pf', JSON.stringify(meaningful));
        else next.delete('pf');
        return next;
      },
      { replace: true },
    );
  }, [filters, setSearchParams]);

  // District / Region data for filters
  const { data: districts = [] } = useQuery<District[]>({
    queryKey: ['districts'],
    queryFn: getAllDistricts,
    staleTime: 5 * 60 * 1000,
  });

  const { data: allRegions = [] } = useQuery<Region[]>({
    queryKey: ['regions'],
    queryFn: getAllRegions,
    staleTime: 5 * 60 * 1000,
  });

  // If a district is selected, show only its regions
  const regions = useMemo(
    () => filters.district_id
      ? allRegions.filter((r) => r.district_id === filters.district_id)
      : allRegions,
    [allRegions, filters.district_id]
  );

  // Whether any filter beyond the default is active
  const hasActiveFilters = useMemo(
    () =>
      filters.type !== 'ALL' ||
      filters.payment_status !== undefined ||
      filters.registration_status !== undefined ||
      filters.attendance_status !== undefined ||
      filters.is_mandatory !== undefined ||
      filters.district_id !== undefined ||
      filters.region_id !== undefined ||
      !!filters.missing_receipt ||
      !!filters.missing_signature ||
      !!filters.search,
    [filters]
  );

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(() => parsePageSizeParam(searchParams.get('size')));
  // Sort and page size live in the URL (?sort=name:asc&size=50) like the filters, so refresh and shared links keep the view
  const [sorting, setSorting] = useState<SortingState>(() => parseSortParam(searchParams.get('sort')));
  useEffect(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        const sort = formatSortParam(sorting);
        if (sort) next.set('sort', sort);
        else next.delete('sort');
        if (pageSize !== DEFAULT_PAGE_SIZE) next.set('size', String(pageSize));
        else next.delete('size');
        return next;
      },
      { replace: true },
    );
  }, [sorting, pageSize, setSearchParams]);

  // Everything the server understands; the receipt filter is client-side
  const serverFilters: Filters = { ...filters };
  delete serverFilters.missing_receipt;
  delete serverFilters.missing_signature;
  const { data: fetchedParticipants = [], isLoading, refetch } = useEventParticipants(
    eventId,
    filters.type === 'ALL'
      ? {
          search: filters.search,
          payment_status: filters.payment_status,
          registration_status: filters.registration_status,
          attendance_status: filters.attendance_status,
          is_mandatory: filters.is_mandatory,
          district_id: filters.district_id,
          region_id: filters.region_id,
        }
      : serverFilters
  );
  const { data: statistics } = useEventParticipantStatistics(eventId);
  const {
    unregisterParticipant,
    recalculatePrice,
    updateParticipant,
    isUnregistering,
    isRecalculating,
  } = useParticipantMutations(eventId);
  const { recordPayment, reversePayment, isProcessing: isPaymentProcessing } = usePaymentMutations(eventId);
  const isAdmin = can('event_participant.remove');
  const hasVoting = useEventHasVoting(eventId);
  const { user: authUser } = useAuth();
  const { toast } = useToast();

  const [isRegistrationOpen, setIsRegistrationOpen] = useState(false);
  const [isBatchImportOpen, setIsBatchImportOpen] = useState(false);
  const [deletingParticipantId, setDeletingParticipantId] = useState<string | null>(null);
  const [isBulkCheckInOpen, setIsBulkCheckInOpen] = useState(false);

  // Payment dialogs — lifted out of table rows so Radix Dialog cleanup always runs
  // (row unmount during query refetch would orphan the overlay otherwise)
  const [paymentTarget, setPaymentTarget] = useState<ParticipantWithDetails | null>(null);
  const [reversalTarget, setReversalTarget] = useState<ParticipantWithDetails | null>(null);
  const [reviewTarget, setReviewTarget] = useState<ParticipantWithDetails | null>(null);
  const [isBulkPrintOpen, setIsBulkPrintOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  // Bulk actions on the selection ask for confirmation first (they touch many rows / money)
  const [bulkConfirm, setBulkConfirm] = useState<'checkin' | 'charge' | null>(null);
  const [bulkMethod, setBulkMethod] = useState<EventPaymentMethod>('cash');

  // Visible optional columns, remembered per browser
  const COLUMNS_KEY = 'events.participants.columns';
  const [visibleColumns, setVisibleColumns] = useState<Set<string>>(() => {
    try {
      const saved = localStorage.getItem(COLUMNS_KEY);
      if (saved) return new Set(JSON.parse(saved) as string[]);
    } catch {
      /* storage unavailable: use defaults */
    }
    return new Set(TOGGLEABLE_COLUMNS.filter((c) => c.defaultVisible).map((c) => c.id));
  });
  const toggleColumn = (id: string) =>
    setVisibleColumns((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      try {
        localStorage.setItem(COLUMNS_KEY, JSON.stringify([...next]));
      } catch {
        /* ignore */
      }
      return next;
    });

  // "Revisar pendientes": walk the pending pre-registrations one after another
  const [queue, setQueue] = useState<{ list: ParticipantWithDetails[]; index: number } | null>(null);
  const [queueLoading, setQueueLoading] = useState(false);
  const [receiptTarget, setReceiptTarget] = useState<{
    paymentId: string;
    participant: ParticipantWithDetails;
    amount: number;
    method: EventPaymentMethod;
    /** Open the receipt form straight away (issuing from the row menu) */
    skipPrompt?: boolean;
  } | null>(null);

  // Row-menu targets: single sticker preview, receipt preview, edit form, edit link
  const [editFormTarget, setEditFormTarget] = useState<ParticipantWithDetails | null>(null);
  const [editLinkTarget, setEditLinkTarget] = useState<ParticipantWithDetails | null>(null);
  const canEditForm = can('event_participant.register');
  const [stickerTarget, setStickerTarget] = useState<ParticipantWithDetails | null>(null);
  const [receiptPreview, setReceiptPreview] = useState<Receipt | null>(null);
  const queryClient = useQueryClient();
  const { checkIn, markAbsent, resetStatus } = useAttendanceMutations(eventId);
  const canViewPayments = can('event_payment.view');
  const canIssueReceipts = can('event_payment.record');

  // Which payments still lack a receipt / which receipt to show, per participant (for the row menu)
  const { data: eventPayments = [], isLoading: paymentsLoading } = useQuery({
    queryKey: ['event_payments', 'row-menu', eventId],
    queryFn: () => getEventPayments(eventId),
    enabled: canViewPayments,
  });
  const receiptInfoByParticipant = useMemo(() => {
    const map = new Map<string, ParticipantReceiptInfo>();
    // getEventPayments returns newest first, so the first issued receipt seen is the latest
    for (const pay of eventPayments) {
      if (pay.status !== 'RECORDED') continue;
      const info = map.get(pay.participant_id) ?? {};
      if (!pay.receipt_id) {
        info.paymentWithoutReceipt = { paymentId: pay.id, amount: Number(pay.amount), method: pay.method };
      } else if (pay.receipt?.status === 'issued' && !info.receiptId) {
        info.receiptId = pay.receipt_id;
      }
      map.set(pay.participant_id, info);
    }
    return map;
  }, [eventPayments]);

  // "Pagos sin recibo" is applied here: receipts live in the payments list, not in the participants query
  const participants = useMemo(
    () =>
      fetchedParticipants.filter(
        (p) =>
          (!filters.missing_receipt || receiptInfoByParticipant.get(p.id)?.paymentWithoutReceipt) &&
          (!filters.missing_signature || needsSignature(p, event?.signature_mode)),
      ),
    [fetchedParticipants, filters.missing_receipt, filters.missing_signature, receiptInfoByParticipant, event?.signature_mode],
  );
  const gridLoading = isLoading || (!!filters.missing_receipt && paymentsLoading);

  // Bulk authority registration
  const [isBulkRegistering, setIsBulkRegistering] = useState(false);
  const [bulkProgress, setBulkProgress] = useState<{ current: number; total: number } | null>(null);
  const [authorityScope, setAuthorityScope] = useState<'ALL' | 'DISTRICT' | 'REGION'>('ALL');
  const [showBulkConfirmDialog, setShowBulkConfirmDialog] = useState(false);


  // Handlers
  const handleBulkRegisterAuthorities = async () => {
    setShowBulkConfirmDialog(false);
    setIsBulkRegistering(true);
    setBulkProgress({ current: 0, total: 0 });

    try {
      const result = await eventParticipantsRepository.bulkRegisterAuthorities(
        eventId,
        authorityScope,
        (current, total) => setBulkProgress({ current, total })
      );

      await refetch();

      toast({
        title: 'Autoridades registradas',
        description: `${result.registered} registrados, ${result.skipped} ya existían${result.errors > 0 ? `, ${result.errors} errores` : ''}`,
      });
    } catch (error) {
      console.error('Error bulk registering authorities:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron registrar las autoridades',
        variant: 'destructive',
      });
    } finally {
      setIsBulkRegistering(false);
      setBulkProgress(null);
    }
  };

  const showQueueItem = async (list: ParticipantWithDetails[], index: number) => {
    // Re-read the participant: another reviewer may have already handled it
    const fresh = await getParticipantById(list[index].id).catch(() => null);
    setQueue({ list, index });
    setReviewTarget(fresh ?? list[index]);
  };

  const startReviewQueue = async () => {
    setQueueLoading(true);
    try {
      const pending = await getEventParticipants(eventId, { registration_status: 'PRE_REGISTERED' });
      if (pending.length === 0) {
        toast({ title: 'No hay inscripciones por revisar' });
        return;
      }
      const ordered = [...pending].sort((a, b) => a.registration_date.localeCompare(b.registration_date));
      await showQueueItem(ordered, 0);
    } catch (err) {
      toast({ title: err instanceof Error ? err.message : 'No se pudo cargar la cola', variant: 'destructive' });
    } finally {
      setQueueLoading(false);
    }
  };

  const reviewQueue: ReviewQueue | null = queue
    ? {
        index: queue.index,
        total: queue.list.length,
        onPrev: () => void showQueueItem(queue.list, Math.max(0, queue.index - 1)),
        onNext: () => void showQueueItem(queue.list, Math.min(queue.list.length - 1, queue.index + 1)),
        onReviewed: () => {
          if (queue.index + 1 < queue.list.length) {
            void showQueueItem(queue.list, queue.index + 1);
          } else {
            setQueue(null);
            setReviewTarget(null);
            toast({ title: 'No quedan inscripciones por revisar' });
          }
        },
      }
    : null;

  const deletingParticipant = participants.find((p) => p.id === deletingParticipantId);
  const deletingParticipantName = deletingParticipant?.minister?.full_name ?? deletingParticipant?.guest?.full_name;

  const handleDelete = async () => {
    if (!deletingParticipantId) return;
    await unregisterParticipant(deletingParticipantId);
    setDeletingParticipantId(null);
  };

  const handleExport = useCallback(() => {
    exportParticipantsToCSV(participants, eventName);
    toast({ title: 'Exportación completada', description: 'El archivo CSV ha sido descargado' });
  }, [participants, eventName, toast]);

  const handleRecalculatePrice = useCallback((participantId: string) => {
    recalculatePrice(participantId);
  }, [recalculatePrice]);

  const attendanceToast = useCallback(
    async (run: () => Promise<unknown>, title: string, p: ParticipantWithDetails, description: string) => {
      const name = p.minister?.full_name ?? p.guest?.full_name ?? 'Participante';
      try {
        await run();
        toast({ title, description: `${name} ${description}` });
      } catch {
        // error toast handled by the mutation
      }
    },
    [toast],
  );

  const openReceipt = useCallback(
    async (receiptId: string) => {
      try {
        setReceiptPreview(await getReceiptById(receiptId));
      } catch (err) {
        toast({ title: err instanceof Error ? err.message : 'No se pudo abrir el recibo', variant: 'destructive' });
      }
    },
    [toast],
  );

  const issueReceipt = useCallback(
    (participant: ParticipantWithDetails, payment: NonNullable<ParticipantReceiptInfo['paymentWithoutReceipt']>) =>
      setReceiptTarget({
        paymentId: payment.paymentId,
        participant,
        amount: payment.amount,
        method: (payment.method ?? 'cash') as EventPaymentMethod,
        skipPrompt: true,
      }),
    [],
  );

  // After verifying a registration, open the receipt form: for the payment recorded in the same
  // step, else for a recorded payment of this person that still has no receipt
  const offerReceiptAfterVerify = useCallback(
    (
      participant: ParticipantWithDetails,
      recorded: { paymentId: string; amount: number; method: EventPaymentMethod } | null,
    ) => {
      queryClient.invalidateQueries({ queryKey: ['event_payments'] });
      if (recorded) {
        issueReceipt(participant, recorded);
        return;
      }
      const pending = receiptInfoByParticipant.get(participant.id)?.paymentWithoutReceipt;
      if (pending) issueReceipt(participant, pending);
    },
    [queryClient, issueReceipt, receiptInfoByParticipant],
  );

  // One action list per row, shared by the ⋮ button and the right-click menu
  const getActions = useCallback(
    (p: ParticipantWithDetails) =>
      buildParticipantActions(p, {
        canOperate,
        canIssueReceipts,
        canEditForm,
        signatureMode: event?.signature_mode,
        isAdmin,
        receiptInfo: receiptInfoByParticipant.get(p.id),
        handlers: {
          onReview: setReviewTarget,
          onRequestPayment: setPaymentTarget,
          onRequestReversal: setReversalTarget,
          onIssueReceipt: issueReceipt,
          onViewReceipt: openReceipt,
          onCheckIn: (participant) =>
            attendanceToast(() => checkIn(participant.id), 'Asistencia registrada', participant, 'marcado como presente'),
          onMarkAbsent: (participant) =>
            attendanceToast(() => markAbsent(participant.id), 'Marcado como ausente', participant, 'marcado como ausente'),
          onResetAttendance: (participant) =>
            attendanceToast(() => resetStatus(participant.id), 'Estado reiniciado', participant, 'volvió a pendiente'),
          onPrintSticker: setStickerTarget,
          onEditForm: setEditFormTarget,
          onEditLink: setEditLinkTarget,
          onSetVoting: canEditForm && hasVoting
            ? async (participant, enabled) => {
                try {
                  await setVotingEnabled([participant.id], enabled);
                  toast({
                    title: enabled ? 'Habilitado para votar' : 'Habilitación para votar quitada',
                    description: participant.minister?.full_name ?? participant.guest?.full_name ?? undefined,
                  });
                  await refetch();
                } catch (err) {
                  toast({ title: err instanceof Error ? err.message : 'No se pudo cambiar', variant: 'destructive' });
                }
              }
            : undefined,
          onToggleMandatory: (participant) =>
            updateParticipant({ id: participant.id, data: { is_mandatory: !participant.is_mandatory } }),
          onRecalculatePrice: handleRecalculatePrice,
          onDelete: (id) => setDeletingParticipantId(id),
        },
      }),
    [canOperate, canIssueReceipts, canEditForm, event?.signature_mode, isAdmin, receiptInfoByParticipant, issueReceipt, openReceipt, attendanceToast, checkIn, markAbsent,
      resetStatus, updateParticipant, handleRecalculatePrice, refetch, toast, hasVoting],
  );

  // Close the modal only on success so the entered amount/reason survives an error
  // (the mutation's onError already shows the destructive toast).
  const handleRecordPayment = async (amount: number, notes: string | undefined, method: EventPaymentMethod) => {
    if (!paymentTarget) return;
    try {
      const paymentId = await recordPayment({ participant_id: paymentTarget.id, payment_amount: amount, notes, method });
      // Offer the official receipt right away (receipts module, linked to this payment)
      setReceiptTarget({ paymentId, participant: paymentTarget, amount, method });
      setPaymentTarget(null);
      queryClient.invalidateQueries({ queryKey: ['event_payments'] });
      toast({ title: 'Pago registrado', description: `Pago de ${formatPrice(amount)} registrado exitosamente` });
    } catch {
      // handled by mutation onError
    }
  };

  const handleReversePayment = async (reason: string) => {
    if (!reversalTarget) return;
    const amount = reversalTarget.payment_amount ?? reversalTarget.calculated_price ?? 0;
    try {
      await reversePayment({ participant_id: reversalTarget.id, reversal_reason: reason });
      setReversalTarget(null);
      queryClient.invalidateQueries({ queryKey: ['event_payments'] });
      toast({ title: 'Pago revertido', description: `Pago de ${formatPrice(amount)} revertido exitosamente` });
    } catch {
      // handled by mutation onError
    }
  };

  const handleBulkCheckInComplete = useCallback(async () => {
    await refetch();
    setIsBulkCheckInOpen(false);
  }, [refetch]);

  // Stable search handler — must not be recreated each render
  // because SearchInput's debounce effect has onChange in its deps array
  const handleSearchChange = useCallback((value: string) => {
    setFilters(prev => ({ ...prev, search: value || undefined }));
  }, []);

  // Sorting runs on the whole filtered list, then we paginate (so it orders across pages)
  const sortedParticipants = useMemo(() => sortParticipants(participants, sorting), [participants, sorting]);
  const totalPages = Math.ceil(sortedParticipants.length / pageSize);
  const totalItems = sortedParticipants.length;
  const pagedParticipants = useMemo(
    () => sortedParticipants.slice((currentPage - 1) * pageSize, currentPage * pageSize),
    [sortedParticipants, currentPage, pageSize],
  );

  const pageSelectionState: 'all' | 'some' | 'none' = pagedParticipants.length > 0 && pagedParticipants.every((p) => selectedIds.has(p.id))
    ? 'all'
    : pagedParticipants.some((p) => selectedIds.has(p.id)) ? 'some' : 'none';
  const selectedParticipants = useMemo(
    () => participants.filter((p) => selectedIds.has(p.id)),
    [participants, selectedIds],
  );

  const bulkSummary = useMemo(() => {
    const outstanding = (p: ParticipantWithDetails) =>
      Math.max(0, Number(p.calculated_price ?? 0) - Number(p.payment_amount ?? 0));
    const chargeable = selectedParticipants.filter((p) => p.payment_status !== 'PAID' && outstanding(p) > 0);
    return {
      toCheckIn: selectedParticipants.filter((p) => p.attendance_status !== 'ATTENDED').length,
      chargeCount: chargeable.length,
      chargeTotal: chargeable.reduce((sum, p) => sum + outstanding(p), 0),
      unverified: selectedParticipants.filter((p) => p.registration_status !== 'VERIFIED').length,
      // Delegates never vote: the bulk action skips them
      votable: selectedParticipants.filter((p) => !p.guest?.is_delegate).length,
    };
  }, [selectedParticipants]);

  const runBulk = async (label: string, action: () => Promise<{ success: number; failed: number }>) => {
    setBulkBusy(true);
    try {
      const r = await action();
      toast({ title: label, description: `${r.success} correctos${r.failed ? `, ${r.failed} con error` : ''}` });
      setSelectedIds(new Set());
      await refetch();
    } catch (err) {
      toast({ title: err instanceof Error ? err.message : 'Error en la acción masiva', variant: 'destructive' });
    } finally {
      setBulkBusy(false);
    }
  };

  // Column definitions with actions
  const columns = useMemo(
    () =>
      getParticipantColumns({
        getActions,
        onRequestPayment: setPaymentTarget,
        onReview: setReviewTarget,
        getReceiptInfo: (p) => receiptInfoByParticipant.get(p.id),
        onIssueReceipt: issueReceipt,
        canIssueReceipts,
        signatureMode: event?.signature_mode,
        onRequestSignature: setEditLinkTarget,
        canEditForm,
        showVoting: hasVoting,
        selection: {
          isSelected: (id) => selectedIds.has(id),
          toggle: (id) =>
            setSelectedIds((prev) => {
              const next = new Set(prev);
              if (next.has(id)) next.delete(id);
              else next.add(id);
              return next;
            }),
          pageState: pageSelectionState,
          togglePage: () =>
            setSelectedIds((prev) => {
              const next = new Set(prev);
              const all = pagedParticipants.every((p) => next.has(p.id));
              pagedParticipants.forEach((p) => (all ? next.delete(p.id) : next.add(p.id)));
              return next;
            }),
        },
        isUpdating: isUnregistering || isRecalculating,
        canOperate,
      }),
    [getActions, receiptInfoByParticipant, issueReceipt, canIssueReceipts, event?.signature_mode, canEditForm, isUnregistering, isRecalculating, canOperate,
      selectedIds, pageSelectionState, pagedParticipants, hasVoting]
  );

  // Name / selection / actions are always shown; the rest follow the "Columnas" menu
  const toggleableIds = useMemo(() => new Set(TOGGLEABLE_COLUMNS.map((c) => c.id)), []);
  const shownColumns = useMemo(
    () => columns.filter((c) => !c.id || !toggleableIds.has(c.id) || visibleColumns.has(c.id)),
    [columns, toggleableIds, visibleColumns],
  );

  // Any filter/search change → back to page 1
  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  // Show empty onboarding card only when there's truly no data and no filters applied
  const showEmptyCard = !isLoading && participants.length === 0 && !hasActiveFilters;


  return (
    <div className="space-y-6">
      {/* Header with Actions */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold">Participantes registrados</h3>
          <p className="text-sm text-muted-foreground">
            Gestiona las inscripciones de ministros, delegados e invitados
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {(statistics?.pre_registered ?? 0) > 0 && (
            <Button
              variant="outline"
              className="border-amber-500 text-amber-700 hover:bg-amber-50 hover:text-amber-800"
              onClick={startReviewQueue}
              disabled={queueLoading}
            >
              {queueLoading ? <Loader2 className="mr-2 size-4 animate-spin" /> : <FileSearch className="mr-2 size-4" />}
              Revisar pendientes ({statistics?.pre_registered})
            </Button>
          )}

          {statusAllowsOps && canAny('event_participant.scan', 'event_participant.checkin') ? (
            <Button variant="outline" asChild>
              <a href={`/checkin/${eventId}`} target="_blank" rel="noreferrer">
                <ScanLine className="mr-2 size-4" />
                Check-in
              </a>
            </Button>
          ) : (
            <Button variant="outline" disabled>
              <ScanLine className="mr-2 size-4" />
              Check-in
            </Button>
          )}

          {can('event_participant.register') && (
            <Button variant="outline" asChild>
              <a href={`/registro/${eventId}`} target="_blank" rel="noreferrer">
                <UserPlus className="mr-2 size-4" />
                Mesa de registro
              </a>
            </Button>
          )}

          <Button
            variant="outline"
            onClick={() => setIsBulkPrintOpen(true)}
            disabled={participants.length === 0 || !event}
          >
            <Printer className="mr-2 size-4" />
            Imprimir
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" disabled={isBulkRegistering}>
                {isBulkRegistering ? (
                  <Loader2 className="mr-2 size-4 animate-spin" />
                ) : (
                  <MoreHorizontal className="mr-2 size-4" />
                )}
                Más
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={() => setIsBulkCheckInOpen(true)}
                disabled={participants.length === 0 || !canOperate}
              >
                <UserCheck className="mr-2 size-4" />
                Registro masivo de asistencia
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setIsBatchImportOpen(true)} disabled={!canRegister}>
                <FileUp className="mr-2 size-4" />
                Importar CSV
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={handleExport}
                disabled={participants.length === 0 || !can('event_participant.export')}
              >
                <Download className="mr-2 size-4" />
                Exportar CSV
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setShowBulkConfirmDialog(true)} disabled={!canRegister}>
                <UserCog className="mr-2 size-4" />
                Registrar autoridades
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Primary action */}
          <Button onClick={() => setIsRegistrationOpen(true)} disabled={!canRegister}>
            <UserPlus className="mr-2 size-4" />
            Registrar participante
          </Button>
        </div>
      </div>

      {!acceptsRegistrations && (
        <Alert>
          <AlertDescription className="flex flex-wrap items-center justify-between gap-3">
            <span>
              {eventStatus === 'DRAFT'
                ? 'El evento está en borrador: no se pueden registrar participantes hasta abrir las inscripciones.'
                : eventStatus === 'CLOSED'
                  ? 'Inscripciones cerradas. Aún puede registrar pagos y asistencia.'
                  : 'Evento completado. Solo lectura.'}
            </span>
            {(eventStatus === 'DRAFT' || eventStatus === 'CLOSED') && onRequestStatus && (
              <span title={canTransition ? undefined : 'No tiene permiso para cambiar el estado del evento'}>
                <Button size="sm" disabled={!canTransition} onClick={() => onRequestStatus('OPEN')}>
                  {eventStatus === 'CLOSED' ? 'Reabrir inscripciones' : 'Abrir inscripciones'}
                </Button>
              </span>
            )}
          </AlertDescription>
        </Alert>
      )}

      {/* Statistics Cards */}
      {/* Event-wide totals (independent of the table filters); click applies a filter */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-5">
        {!statistics ? (
          Array.from({ length: 5 }).map((_, i) => <StatCardSkeleton key={i} />)
        ) : (
          <>
            <StatCard
              title="Total de participantes"
              value={statistics.total}
              icon={Users}
              description={`${statistics.ministers} ministros, ${statistics.guests} invitados`}
              onClick={() => setFilters({ type: 'ALL' })}
            />
            <StatCard
              title="Por revisar"
              value={statistics.pre_registered}
              icon={FileSearch}
              description={`${statistics.verified} verificadas · ${statistics.rejected} rechazadas`}
              onClick={() => setFilters({ type: 'ALL', registration_status: 'PRE_REGISTERED' })}
            />
            <StatCard
              title="Recaudado"
              value={<Money value={statistics.collected} />}
              icon={DollarSign}
              description={`${statistics.paid} pagados · ${statistics.partial} parciales`}
              onClick={() => setFilters({ type: 'ALL', payment_status: 'PAID' })}
            />
            <StatCard
              title="Por cobrar"
              value={<Money value={statistics.outstanding} />}
              icon={TrendingUp}
              description={`${statistics.pending} pendientes`}
              onClick={() => setFilters({ type: 'ALL', payment_status: 'PENDING' })}
            />
            <StatCard
              title="Asistencia"
              value={`${statistics.attended}/${statistics.total}`}
              icon={UserCheck}
              description={
                statistics.mandatory_absent > 0
                  ? `${statistics.mandatory_absent} obligatorios sin asistir`
                  : `${statistics.total > 0 ? Math.round((statistics.attended / statistics.total) * 100) : 0}% asistencia`
              }
              onClick={() => setFilters({ type: 'ALL', attendance_status: 'ATTENDED' })}
            />
          </>
        )}
      </div>

      {/* Bulk authority registration progress */}
      {isBulkRegistering && (
        <div className="flex items-center gap-3 rounded-md border bg-muted/50 px-4 py-3 text-sm">
          <Loader2 className="size-4 shrink-0 animate-spin text-primary" />
          <span className="text-muted-foreground">
            Registrando autoridades
            {bulkProgress && bulkProgress.total > 0
              ? ` (${bulkProgress.current} de ${bulkProgress.total})`
              : '...'}
          </span>
          {bulkProgress && bulkProgress.total > 0 && (
            <Progress
              value={(bulkProgress.current / bulkProgress.total) * 100}
              className="w-32"
            />
          )}
        </div>
      )}

      {selectedIds.size > 0 && (
        <div className="sticky top-0 z-10 flex flex-wrap items-center gap-2 rounded-md border bg-background p-3 shadow-sm">
          <span className="mr-2 text-sm font-medium">{selectedIds.size} seleccionados</span>
          <Button
            size="sm"
            variant="outline"
            disabled={bulkBusy || !canOperate}
            onClick={() => setBulkConfirm('checkin')}
          >
            <UserCheck className="mr-2 size-4" /> Registrar asistencia
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={bulkBusy || !canOperate || !can('event_payment.record')}
            onClick={() => setBulkConfirm('charge')}
          >
            <DollarSign className="mr-2 size-4" /> Cobrar saldo
          </Button>
          {canEditForm && hasVoting && (
            <>
              <Button
                size="sm"
                variant="outline"
                disabled={bulkBusy || bulkSummary.votable === 0}
                onClick={() =>
                  void runBulk('Habilitados para votar', async () => {
                    const ids = selectedParticipants.filter((p) => !p.guest?.is_delegate).map((p) => p.id);
                    const changed = await setVotingEnabled(ids, true);
                    return { success: changed, failed: 0 };
                  })
                }
              >
                <Vote className="mr-2 size-4" /> Habilitar para votar
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={bulkBusy || bulkSummary.votable === 0}
                onClick={() =>
                  void runBulk('Habilitación para votar quitada', async () => {
                    const ids = selectedParticipants.filter((p) => !p.guest?.is_delegate).map((p) => p.id);
                    const changed = await setVotingEnabled(ids, false);
                    return { success: changed, failed: 0 };
                  })
                }
              >
                <Ban className="mr-2 size-4" /> Quitar voto
              </Button>
            </>
          )}
          <Button
            size="sm"
            variant="outline"
            disabled={bulkBusy}
            onClick={() => exportParticipantsToCSV(selectedParticipants, eventName)}
          >
            <Download className="mr-2 size-4" /> Exportar
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setSelectedIds(new Set())} disabled={bulkBusy}>
            Limpiar selección
          </Button>
          {bulkBusy && <Loader2 className="size-4 animate-spin text-muted-foreground" />}
        </div>
      )}

      {/* Filters — kept OUTSIDE DataTableView to prevent SearchInput from re-rendering
          on every filter change (its no-dep useEffect steals focus if it re-renders) */}
      <ParticipantFilterBar
        filters={filters}
        onChange={setFilters}
        districts={districts}
        regions={regions}
        hasActiveFilters={hasActiveFilters}
        canFilterReceipts={canViewPayments}
        canFilterSignature={event?.signature_mode === 'DIGITAL'}
      />

      {/* Participants List */}
      {showEmptyCard ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Users className="mx-auto mb-4 size-12 text-muted-foreground" />
            <h3 className="mb-2 text-lg font-semibold">Sin participantes</h3>
            <p className="mb-4 text-sm text-muted-foreground">
              No hay participantes registrados para este evento
            </p>
            <Button onClick={() => setIsRegistrationOpen(true)} disabled={!canRegister}>
              <UserPlus className="mr-2 size-4" />
              Registrar primer participante
            </Button>
          </CardContent>
        </Card>
      ) : (
        isMobile ? (
          <ParticipantCardList
            eventId={eventId}
            participants={participants}
            isLoading={gridLoading}
            searchValue={filters.search || ''}
            onSearchChange={handleSearchChange}
            onOpen={(p) => {
              setQueue(null);
              setReviewTarget(p);
            }}
            onRequestPayment={setPaymentTarget}
            onRequestReversal={setReversalTarget}
            canOperate={canOperate}
            emptyMessage={hasActiveFilters ? 'Sin resultados para los filtros aplicados' : undefined}
          />
        ) : (
          <DataTableView
            data={pagedParticipants}
            columns={shownColumns}
            onRowClick={(p) => {
              setQueue(null);
              setReviewTarget(p);
            }}
            getRowClassName={() => 'group cursor-pointer'}
            stickyHeader
            rowWrapper={(p, tr) => <ParticipantRowContextMenu key={p.id} row={tr} getGroups={() => getActions(p)} />}
            sorting={sorting}
            onSortingChange={(next) => {
              setSorting(next);
              setCurrentPage(1);
            }}
            toolbarActions={
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="gap-2">
                    <Columns className="size-4" />
                    Columnas
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-56 space-y-2">
                  <p className="text-xs font-medium text-muted-foreground">Mostrar columnas</p>
                  {TOGGLEABLE_COLUMNS.map((c) => (
                    <label key={c.id} className="flex items-center gap-2 text-sm">
                      <Checkbox checked={visibleColumns.has(c.id)} onCheckedChange={() => toggleColumn(c.id)} />
                      {c.label}
                    </label>
                  ))}
                </PopoverContent>
              </Popover>
            }
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setCurrentPage(1);
            }}
            searchValue={filters.search || ''}
            onSearchChange={handleSearchChange}
            searchPlaceholder="Buscar por nombre, código o DNI..."
            isLoading={gridLoading}
            emptyMessage={hasActiveFilters ? 'Sin resultados para los filtros aplicados' : undefined}
          />
        )
      )}

      {/* Registration Dialog */}
      <ParticipantRegistrationDialog
        eventId={eventId}
        basePrice={basePrice}
        open={isRegistrationOpen}
        onOpenChange={setIsRegistrationOpen}
      />

      {/* Batch Import Dialog */}
      <BatchImportDialog
        eventId={eventId}
        open={isBatchImportOpen}
        onOpenChange={setIsBatchImportOpen}
        onImportComplete={() => refetch()}
      />

      {/* Bulk Check-In Dialog */}
      <BulkCheckInDialog
        open={isBulkCheckInOpen}
        onOpenChange={setIsBulkCheckInOpen}
        eventId={eventId}
        mode="all-pending"
        onComplete={handleBulkCheckInComplete}
      />

      {/* Bulk action confirmation (check-in / charge outstanding balance of the selection) */}
      <AlertDialog open={bulkConfirm !== null} onOpenChange={(open) => { if (!open) setBulkConfirm(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {bulkConfirm === 'charge'
                ? `¿Cobrar el saldo de ${bulkSummary.chargeCount} participante(s)?`
                : `¿Registrar la asistencia de ${bulkSummary.toCheckIn} participante(s)?`}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-muted-foreground">
                {bulkConfirm === 'charge' ? (
                  <>
                    <p>
                      Se registrará un pago por el saldo pendiente de cada uno:{' '}
                      <strong className="text-foreground">{formatBs(bulkSummary.chargeTotal)}</strong> en total.
                    </p>
                    {selectedIds.size - bulkSummary.chargeCount > 0 && (
                      <p>{selectedIds.size - bulkSummary.chargeCount} seleccionado(s) ya pagaron y se omitirán.</p>
                    )}
                  </>
                ) : (
                  <p>
                    {selectedIds.size - bulkSummary.toCheckIn > 0
                      ? `${selectedIds.size - bulkSummary.toCheckIn} seleccionado(s) ya tienen asistencia y se omitirán.`
                      : 'Se marcará como presentes a los participantes seleccionados.'}
                  </p>
                )}
                {bulkSummary.unverified > 0 && (
                  <p className="text-amber-700">
                    {bulkSummary.unverified} seleccionado(s) aún no están verificados.
                  </p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>

          {bulkConfirm === 'charge' && (
            <div className="flex flex-col gap-2 py-2">
              <Label className="text-sm">Método de pago</Label>
              <Select value={bulkMethod} onValueChange={(v) => setBulkMethod(v as EventPaymentMethod)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SELECTABLE_PAYMENT_METHODS.map((m) => (
                    <SelectItem key={m} value={m}>
                      {PAYMENT_METHOD_LABELS[m]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              disabled={bulkConfirm === 'charge' ? bulkSummary.chargeCount === 0 : bulkSummary.toCheckIn === 0}
              onClick={() => {
                const action = bulkConfirm;
                setBulkConfirm(null);
                if (action === 'charge') {
                  void runBulk('Pagos registrados', () =>
                    bulkRecordPayments({ participant_ids: [...selectedIds], method: bulkMethod }),
                  );
                } else if (action === 'checkin') {
                  void runBulk('Asistencia registrada', () =>
                    bulkCheckInParticipants({ participant_ids: [...selectedIds], checked_in_by: authUser?.id }),
                  );
                }
              }}
            >
              Confirmar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deletingParticipantId} onOpenChange={() => setDeletingParticipantId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar a {deletingParticipantName ?? 'este participante'}?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. {deletingParticipantName ?? 'El participante'} será eliminado del
              evento.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Authority Registration Confirmation Dialog */}
      <AlertDialog open={showBulkConfirmDialog} onOpenChange={setShowBulkConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Registrar todas las autoridades?</AlertDialogTitle>
            <AlertDialogDescription>
              Se registrarán automáticamente como participantes obligatorios. Los participantes
              ya registrados serán omitidos.
            </AlertDialogDescription>
          </AlertDialogHeader>

          {/* Scope selector inside the dialog */}
          <div className="flex flex-col gap-2 py-2">
            <Label className="text-sm">Alcance del registro</Label>
            <Select
              value={authorityScope}
              onValueChange={(v) => setAuthorityScope(v as 'ALL' | 'DISTRICT' | 'REGION')}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">Todos (Distritos + Regiones)</SelectItem>
                <SelectItem value="DISTRICT">Solo autoridades de Distritos</SelectItem>
                <SelectItem value="REGION">Solo autoridades de Regiones</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleBulkRegisterAuthorities}>
              Continuar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Payment Modal — lives here, not inside the table row, so Radix cleanup
          always runs even when the row unmounts due to query refetch */}
      <PaymentModal
        open={!!paymentTarget}
        onOpenChange={(open) => { if (!open) setPaymentTarget(null); }}
        participantName={
          paymentTarget?.minister?.full_name ??
          paymentTarget?.guest?.full_name ??
          'Participante'
        }
        calculatedPrice={paymentTarget?.calculated_price ?? 0}
        paidSoFar={paymentTarget?.payment_amount ?? 0}
        onSubmit={handleRecordPayment}
        isProcessing={isPaymentProcessing}
      />

      {receiptTarget && (
        <IssueEventReceiptPrompt
          eventName={eventName}
          paymentId={receiptTarget.paymentId}
          participant={receiptTarget.participant}
          amount={receiptTarget.amount}
          method={receiptTarget.method}
          skipPrompt={receiptTarget.skipPrompt}
          onClose={() => setReceiptTarget(null)}
        />
      )}

      {stickerTarget && (
        <BadgeStickerPrint stickers={[participantToSticker(stickerTarget, hasVoting)]} onClose={() => setStickerTarget(null)} />
      )}

      {receiptPreview && (
        <ReceiptPrintPreview
          data={receiptToPrintData(receiptPreview, publicReceiptUrl(receiptPreview.public_token))}
          share={(() => {
            const owner = fetchedParticipants?.find((p) => receiptInfoByParticipant.get(p.id)?.receiptId === receiptPreview.id);
            return owner ? eventReceiptShare(owner, participantPhone(owner), receiptPreview.number) : undefined;
          })()}
          onClose={() => setReceiptPreview(null)}
        />
      )}

      {event && (
        <BulkPrintDialog
          open={isBulkPrintOpen}
          onOpenChange={setIsBulkPrintOpen}
          event={event}
          participants={participants}
        />
      )}

      <EditRegistrationDialog
        eventId={eventId}
        participant={editFormTarget}
        onOpenChange={(open) => { if (!open) setEditFormTarget(null); }}
      />

      <EditLinkDialog
        participant={editLinkTarget}
        eventName={eventName}
        signatureMode={event?.signature_mode}
        onOpenChange={(open) => { if (!open) setEditLinkTarget(null); }}
      />

      <PreregistrationReviewDialog
        eventId={eventId}
        eventName={event?.name ?? eventName}
        participant={reviewTarget}
        signatureMode={event?.signature_mode}
        onEditForm={canEditForm ? (p) => { setReviewTarget(null); setEditFormTarget(p); } : undefined}
        onRequestLink={canEditForm ? (p) => { setReviewTarget(null); setEditLinkTarget(p); } : undefined}
        onVerified={canIssueReceipts ? offerReceiptAfterVerify : undefined}
        queue={reviewQueue}
        onOpenChange={(open) => {
          if (!open) {
            setReviewTarget(null);
            setQueue(null);
          }
        }}
      />

      {/* Payment Reversal Modal */}
      <PaymentReversalModal
        open={!!reversalTarget}
        onOpenChange={(open) => { if (!open) setReversalTarget(null); }}
        participantName={
          reversalTarget?.minister?.full_name ??
          reversalTarget?.guest?.full_name ??
          'Participante'
        }
        paymentAmount={reversalTarget?.payment_amount ?? reversalTarget?.calculated_price ?? 0}
        onSubmit={handleReversePayment}
        isProcessing={isPaymentProcessing}
      />
    </div>
  );
}

